/* 一般外科病房共用美術：病床特寫、引流袋特寫、TPR 單，以及病房家具。 */
import {
  closeZoom,
  type Ctx,
  cyc,
  dither,
  DRAW,
  type DrawFn,
  focusInput,
  type Hotspot,
  INK,
  obox,
  pell,
  pline,
  portraitCanvas,
  ptext,
  R,
  sprite,
  tr,
} from "../../engine";

/* ---------- drain fluid colours ---------- */

/** Common drain / urine colours, light to worrying. */
export const FLUID = {
  serous: "#efe2a6",
  serosang: "#e6a58c",
  bloody: "#b83a3a",
  bilious: "#9aa83a",
  murky: "#8a8a3e",
  chyle: "#f4f0e2",
  urine: "#f2d65a",
  urineDark: "#d89a2a",
} as const;

/* ---------- ward bed close-up ---------- */

/** Drain bag hanging off the bed rail. */
export interface BagView {
  color: string;
  /** Fill 0..1 of the visible bag. */
  level: number;
  /** 0..1 turbidity (dithered specks). */
  cloudy?: number;
}

export interface WardBedScene {
  /** Patient cast key (portrait). */
  pt: string;
  /** Portrait palette override (pale, flushed, jaundiced…). */
  pale?: Record<string, string>;
  wall?: string;
  wall2?: string;
  floor?: string;
  floor2?: string;
  /** Hospital gown colour. */
  gown?: string;
  blanket?: string;
  blanket2?: string;
  /** IV bag colour; null = no line. */
  iv?: string | null;
  /** Second bag piggy-backed (antibiotic, blood…). */
  iv2?: string | null;
  drain?: BagView | null;
  foley?: BagView | null;
  /** Nasal cannula. */
  o2?: boolean;
  /** Dressing over the midline wound; "stained" shows ooze. */
  dressing?: "clean" | "stained" | null;
  /** Small laparoscopic port dressings instead of a midline one. */
  ports?: boolean;
  shiver?: boolean;
  /** Head of bed raised (sitting up). */
  sitting?: boolean;
  /** Bedside whiteboard: NPO sign, water cup. */
  npo?: boolean;
  water?: boolean;
  /** Digits written next to the cup, e.g. "30" (mL/hr). */
  waterNote?: string;
  /** A cup of water on the bedside table. */
  cup?: boolean;
  /** Cast key of someone at the bedside. */
  companion?: string;
  /** Morning light through the window. */
  dawn?: boolean;
  extra?(c: Ctx, now: number): void;
}

function bag(c: Ctx, x: number, y: number, w: number, h: number, v: BagView, now: number): void {
  obox(c, "#eef3f6", x, y, w, h, "#5a6070");
  const fh = Math.max(1, Math.round((h - 2) * Math.min(1, Math.max(0, v.level))));
  R(c, v.color, x + 1, y + h - 1 - fh, w - 2, fh);
  if (v.cloudy) dither(c, x + 1, y + h - 1 - fh, w - 2, fh, "#5a5a2a", v.cloudy * 0.5);
  // ripple on the surface
  if (Math.floor(now / 600) % 2) R(c, "#ffffff55", x + 2, y + h - 1 - fh, w - 4, 1);
  for (let k = 1; k < 4; k++) R(c, "#8a90a2", x + w - 3, y + Math.round((h * k) / 4), 2, 1);
}

export function drawWardBed(c: Ctx, now: number, o: WardBedScene): void {
  const wall = o.wall ?? "#e6ece8";
  const wall2 = o.wall2 ?? "#d8e0db";
  R(c, wall, 0, 0, 192, 70);
  for (let x = 0; x < 192; x += 24) R(c, wall2, x, 0, 1, 70);
  R(c, "#b9c4bd", 0, 68, 192, 3);
  R(c, o.floor ?? "#d6dccf", 0, 71, 192, 37);
  for (let x = 0; x < 192; x += 16) R(c, o.floor2 ?? "#c6cdbf", x, 71, 1, 37);
  dither(c, 0, 94, 192, 14, o.floor2 ?? "#c6cdbf", 0.45);

  // window
  obox(c, "#d6dbf0", 84, 6, 48, 30);
  R(c, o.dawn ? "#f6c98a" : "#9fd0ef", 85, 7, 46, 28);
  if (o.dawn) {
    R(c, "#f9dfae", 85, 7, 46, 10);
    pell(c, 120, 30, 6, 4, "#ffe9a8");
  }
  R(c, "#6a7690", 88, 26, 10, 9);
  R(c, "#7a86a0", 100, 22, 8, 13);
  R(c, "#5a6680", 112, 28, 14, 7);
  R(c, "#d6dbf0", 107, 7, 2, 28);
  R(c, "#c8a0a8", 82, 4, 6, 34);
  R(c, "#c8a0a8", 128, 4, 6, 34);

  // bedside whiteboard
  obox(c, "#ffffff", 6, 3, 48, 30, "#7a8092");
  R(c, "#3b5fa8", 7, 4, 46, 5);
  R(c, "#9aa6c8", 9, 12, 26, 1);
  R(c, "#9aa6c8", 9, 16, 20, 1);
  if (o.npo) {
    obox(c, "#ffffff", 30, 17, 21, 12, "#c0394a");
    ptext(c, "NPO", 32, 21, "#c0394a");
  } else if (o.water) {
    R(c, "#5f86bf", 38, 19, 8, 9);
    R(c, "#9fd0ef", 39, 21, 6, 6);
    if (o.waterNote) ptext(c, o.waterNote, 36, 12, "#3b5fa8");
  }

  // IV pole
  R(c, "#9aa0b2", 66, 2, 2, 72);
  R(c, "#7a8092", 62, 74, 10, 2);
  if (o.iv) {
    obox(c, "#e6f2f8", 60, 6, 7, 12);
    R(c, o.iv, 60, 12, 7, 6);
    pline(c, 63, 18, 63, 40, "#c9dce8");
    pline(c, 63, 40, 44, 52, "#c9dce8");
  }
  if (o.iv2) {
    obox(c, o.iv2, 69, 8, 6, 9);
    pline(c, 72, 17, 66, 34, "#e8d48a");
  }

  if (o.companion) c.drawImage(sprite(o.companion).f0, 150, 26, 40, 40);

  // bedside table
  obox(c, "#c8b08a", 156, 52, 26, 18, "#7a6040");
  R(c, "#b0966e", 156, 58, 26, 1);
  if (o.cup) {
    obox(c, "#e8f2f8", 166, 44, 7, 8, "#5a6070");
    R(c, "#9fd0ef", 167, 47, 5, 4);
  }

  // bed frame & mattress
  obox(c, "#8790a8", 14, 66, 140, 4);
  R(c, "#b8bcc8", 14, 66, 140, 1);
  obox(c, "#5a6070", 20, 71, 4, 20);
  obox(c, "#5a6070", 146, 71, 4, 20);
  obox(c, "#2a2230", 18, 92, 8, 4);
  obox(c, "#2a2230", 144, 92, 8, 4);
  obox(c, "#f4f4f6", 16, 57, 136, 9);
  R(c, "#d9dcea", 16, 64, 136, 2);
  // side rail
  R(c, "#9aa0b2", 40, 60, 70, 2);
  R(c, "#9aa0b2", 42, 60, 2, 8);
  R(c, "#9aa0b2", 106, 60, 2, 8);

  // pillow & head
  const headUp = o.sitting ? -6 : 0;
  obox(c, "#ffffff", 16, 49 + headUp, 30, 9 - headUp);
  const sh = o.shiver ? (Math.floor(now / 70) % 2 ? 1 : -1) : 0;
  c.save();
  c.beginPath();
  c.rect(14, 24 + headUp, 40, 28);
  c.clip();
  c.drawImage(portraitCanvas(o.pt, o.pale ?? {}), 16 + sh, 28 + headUp, 32, 32);
  c.restore();
  if (o.o2) {
    pline(c, 26, 46 + headUp, 38, 46 + headUp, "#cfe8f4");
    pline(c, 38, 46 + headUp, 52, 54, "#cfe8f4");
  }

  // torso in gown
  const gown = o.gown ?? "#a9c8e8";
  obox(c, gown, 46, 50, 66, 9);
  R(c, "#86a8cc", 46, 56, 66, 3);
  for (let x = 50; x < 110; x += 8) R(c, "#c8dcf0", x, 51, 2, 2);
  pell(c, 84, 50, 14, 3, INK);
  pell(c, 84, 50, 13, 2, gown);
  if (o.dressing) {
    obox(c, "#ffffff", 72, 46, 22, 5, "#9aa0b2");
    R(c, "#e8d8a8", 72, 46, 2, 5);
    R(c, "#e8d8a8", 92, 46, 2, 5);
    if (o.dressing === "stained") {
      R(c, "#c8b860", 80, 47, 6, 3);
      dither(c, 78, 46, 10, 5, "#a8a040", 0.4);
    }
  }
  if (o.ports) {
    obox(c, "#ffffff", 76, 47, 4, 3, "#9aa0b2");
    obox(c, "#ffffff", 88, 48, 4, 3, "#9aa0b2");
    obox(c, "#ffffff", 98, 49, 4, 3, "#9aa0b2");
  }
  if (o.shiver) {
    const k = Math.floor(now / 80) % 2;
    R(c, "#ffffff", 48 + k, 49, 1, 1);
    R(c, "#ffffff", 110 - k, 49, 1, 1);
  }

  // blanket over legs
  const bl = o.blanket ?? "#e8e4d4";
  const bl2 = o.blanket2 ?? "#cfc8b0";
  obox(c, bl, 112, 51, 40, 7);
  R(c, bl2, 112, 55, 40, 3);
  pell(c, 144, 51, 6, 2, bl);

  // drain: tube from right flank to a bag on the rail
  if (o.drain) {
    pline(c, 100, 57, 102, 64, "#c9dce8");
    pline(c, 102, 64, 112, 72, "#c9dce8");
    R(c, o.drain.color, 101, 60, 1, 4);
    obox(c, "#9aa0b2", 108, 70, 14, 2);
    bag(c, 106, 73, 18, 22, o.drain, now);
  }
  if (o.foley) {
    pline(c, 116, 60, 126, 66, "#e8e0a0");
    pline(c, 126, 66, 134, 74, "#e8e0a0");
    obox(c, "#9aa0b2", 130, 72, 14, 2);
    bag(c, 128, 75, 18, 20, o.foley, now);
  }

  o.extra?.(c, now);
}

export interface WardHotsCfg {
  ptLabel: string;
  onBoard?: () => void;
  onBelly?: () => void;
  bellyLabel?: string;
  onDressing?: () => void;
  onDrain?: () => void;
  onFoley?: () => void;
  onIv?: () => void;
  onTable?: () => void;
  tableLabel?: string;
  companionLabel?: string;
}

/** Hotspots matching drawWardBed. Earlier entries win where they overlap. */
export function wardHots(o: WardHotsCfg): Hotspot[] {
  const talk = (): void => {
    closeZoom();
    focusInput();
  };
  const hs: Hotspot[] = [];
  if (o.onDressing) hs.push({ x: 70, y: 44, w: 26, h: 9, label: tr("傷口敷料", "Dressing"), fn: o.onDressing });
  if (o.onDrain) hs.push({ x: 96, y: 56, w: 30, h: 40, label: tr("引流管與引流袋", "Drain & bag"), fn: o.onDrain });
  if (o.onFoley) hs.push({ x: 126, y: 64, w: 22, h: 32, label: tr("尿管與尿袋", "Foley & bag"), fn: o.onFoley });
  hs.push({ x: 14, y: 26, w: 36, h: 24, label: o.ptLabel, fn: talk });
  if (o.onBelly)
    hs.push({
      x: 46,
      y: 44,
      w: 66,
      h: 15,
      label: o.bellyLabel ?? tr("肚子（觸診）", "Belly (palpate)"),
      fn: o.onBelly,
    });
  if (o.onBoard) hs.push({ x: 6, y: 3, w: 48, h: 30, label: tr("床頭白板", "Bedside board"), fn: o.onBoard });
  if (o.onIv) hs.push({ x: 58, y: 2, w: 18, h: 36, label: tr("點滴", "IV"), fn: o.onIv });
  if (o.onTable)
    hs.push({ x: 154, y: 44, w: 30, h: 26, label: o.tableLabel ?? tr("床頭櫃", "Bedside table"), fn: o.onTable });
  if (o.companionLabel) hs.push({ x: 150, y: 26, w: 40, h: 26, label: o.companionLabel, fn: talk });
  return hs;
}

/* ---------- drain bag close-up ---------- */

export interface DrainView {
  color: string;
  /** mL in the bag now. */
  ml: number;
  /** Bag capacity in mL (default 500). */
  cap?: number;
  cloudy?: number;
  /** Fibrin strands / debris. */
  flecks?: boolean;
  /** Tag colour on the tube. */
  tag?: string;
  /** Gauze at the exit site, stained in this colour (null = clean). */
  siteStain?: string | null;
  /** Hand milking the tube. */
  milking?: boolean;
}

export function drawDrainBag(c: Ctx, now: number, d: DrainView): void {
  R(c, "#dfe6ea", 0, 0, 192, 108);
  for (let y = 0; y < 108; y += 12) R(c, "#d2dade", 0, y, 192, 1);
  // bed rail
  obox(c, "#9aa0b2", 0, 12, 192, 5, "#5a6070");
  R(c, "#c8ccd6", 0, 12, 192, 1);
  // exit site on skin (left panel)
  obox(c, "#efc8a6", 4, 26, 52, 42, "#b88a68");
  dither(c, 4, 26, 52, 42, "#e2b896", 0.3);
  obox(c, "#ffffff", 14, 36, 30, 22, "#9aa0b2");
  for (let k = 0; k < 4; k++) R(c, "#eef0f4", 16, 39 + k * 5, 26, 1);
  if (d.siteStain) {
    pell(c, 29, 47, 8, 5, d.siteStain);
    dither(c, 18, 40, 22, 14, d.siteStain, 0.25);
  }
  pell(c, 29, 47, 2, 2, "#c9dce8");
  // tube from site to bag
  pline(c, 31, 47, 64, 20, "#c9dce8", 2);
  pline(c, 64, 20, 104, 20, "#c9dce8", 2);
  pline(c, 104, 20, 112, 30, "#c9dce8", 2);
  for (let x = 66; x < 102; x += 6) R(c, d.color, x, 19, 3, 2);
  if (d.tag) obox(c, d.tag, 80, 22, 10, 6);
  if (d.milking) {
    obox(c, "#efc8a6", 84 + (Math.floor(now / 200) % 3) * 4, 15, 9, 9, "#b88a68");
  }
  // hanger
  obox(c, "#7a8092", 100, 14, 30, 3);
  // bag
  const bx = 96;
  const by = 30;
  const bw = 58;
  const bh = 72;
  obox(c, "#f4f8fa", bx, by, bw, bh, "#5a6070");
  R(c, "#e4ecf0", bx, by, bw, 6);
  const cap = d.cap ?? 500;
  const lev = Math.min(1, d.ml / cap);
  const fh = Math.max(2, Math.round((bh - 8) * lev));
  const fy = by + bh - 2 - fh;
  R(c, d.color, bx + 2, fy, bw - 4, fh);
  if (d.cloudy) dither(c, bx + 2, fy, bw - 4, fh, "#4a4a20", d.cloudy * 0.55);
  if (d.flecks)
    for (let k = 0; k < 9; k++) {
      const fx = bx + 6 + ((k * 23 + Math.floor(now / 900)) % (bw - 12));
      const fy2 = fy + 3 + ((k * 13) % Math.max(4, fh - 6));
      R(c, "#f2eed2", fx, fy2, 3, 1);
    }
  R(c, "#ffffff66", bx + 4, fy, bw - 8, 1);
  // graduations
  for (let k = 1; k <= 5; k++) {
    const y = by + bh - 2 - Math.round(((bh - 8) * k) / 5);
    R(c, "#3a3f52", bx + bw - 10, y, 7, 1);
    ptext(c, String((cap / 5) * k), bx + bw + 3, y - 2, "#3a3f52");
  }
  // reading
  obox(c, "#20263a", 4, 78, 52, 22);
  ptext(c, String(Math.round(d.ml)), 8, 82, "#ffd447", 2);
  R(c, d.color, 46, 84, 6, 10);
  // outlet
  R(c, "#7a8092", bx + bw / 2 - 2, by + bh, 4, 5);
}

export interface DrainHotsCfg {
  onFluid: () => void;
  onScale: () => void;
  onSite: () => void;
  onTube: () => void;
}

export function drainHots(o: DrainHotsCfg): Hotspot[] {
  return [
    { x: 6, y: 28, w: 48, h: 38, label: tr("引流管出口的紗布", "Gauze at the drain site"), fn: o.onSite },
    {
      x: 60,
      y: 14,
      w: 50,
      h: 14,
      label: tr("引流管（擠一擠看通不通）", "Tube (milk it to check it’s patent)"),
      fn: o.onTube,
    },
    { x: 150, y: 30, w: 40, h: 72, label: tr("刻度", "Scale"), fn: o.onScale },
    { x: 98, y: 32, w: 52, h: 68, label: tr("引流液（顏色、性狀）", "Fluid (color, character)"), fn: o.onFluid },
  ];
}

/* ---------- TPR sheet ---------- */

export interface TprCol {
  /** Hour label, e.g. "08". */
  hr24: string;
  /** Post-op day the column belongs to (draws a separator on change). */
  day: number;
  temp: number;
  pulse: number;
  sbp: number;
  dbp: number;
  rr: number;
  /** Measured by the clerk this morning (drawn highlighted). */
  mine?: boolean;
}

export interface TprShift {
  /** Intake / urine / drain for an 8-hour shift (mL). */
  input: number;
  urine: number;
  drain?: number;
  drainColor?: string;
}

export interface TprSheet {
  cols: TprCol[];
  /** One entry per shift; drawn under the chart, spread evenly. */
  shifts: TprShift[];
  /** Column index to circle (a trend the clerk has noticed). */
  mark?: number | null;
}

const TX0 = 22;
const TX1 = 186;
const TY0 = 12;
const TY1 = 66;
const tY = (v: number): number => TY1 - ((v - 35) / 6) * (TY1 - TY0);
const pY = (v: number): number => TY1 - ((v - 40) / 120) * (TY1 - TY0);

export function drawTPR(c: Ctx, now: number, s: TprSheet): void {
  R(c, "#fbf8ee", 0, 0, 192, 108);
  R(c, "#3b7a5a", 0, 0, 192, 9);
  ptext(c, "TPR", 4, 2, "#ffffff");
  ptext(c, "T", 30, 2, "#9fd0ef");
  R(c, "#9fd0ef", 36, 4, 6, 1);
  ptext(c, "P", 48, 2, "#ff9a9a");
  R(c, "#ff9a9a", 54, 4, 6, 1);
  ptext(c, "BP", 66, 2, "#d9dcea");
  // grid
  for (let v = 35; v <= 41; v++) {
    const y = Math.round(tY(v));
    R(c, v === 38 ? "#e8b8b8" : "#e4dcc4", TX0, y, TX1 - TX0, 1);
    ptext(c, String(v), 2, y - 2, "#5f86bf");
  }
  for (let v = 60; v <= 160; v += 20) ptext(c, String(v), TX1 - 12, Math.round(pY(v)) - 2, "#c0394a");
  const n = s.cols.length;
  const cw = (TX1 - 14 - TX0) / Math.max(1, n);
  const cx = (i: number): number => Math.round(TX0 + cw * (i + 0.5));
  for (let i = 0; i < n; i++) {
    const col = s.cols[i];
    if (!col) continue;
    const x0 = Math.round(TX0 + cw * i);
    R(c, "#efe8d2", x0, TY0, 1, TY1 - TY0);
    const prev = s.cols[i - 1];
    if (prev && prev.day !== col.day) R(c, "#9a8a6a", x0, TY0 - 2, 1, 72);
    if (col.mine) R(c, "#fff4b8", x0 + 1, TY0, Math.round(cw) - 1, TY1 - TY0);
    ptext(c, col.hr24, cx(i) - 3, TY1 + 2, col.mine ? "#c0394a" : "#7a6a4a");
  }
  // BP bars
  for (let i = 0; i < n; i++) {
    const col = s.cols[i];
    if (!col) continue;
    const x = cx(i) + 2;
    const y1 = Math.round(pY(Math.min(160, col.sbp)));
    const y2 = Math.round(pY(col.dbp));
    R(c, "#8a90a2", x, y1, 1, Math.max(1, y2 - y1));
    R(c, "#5a6070", x - 1, y1, 3, 1);
    R(c, "#5a6070", x - 1, y2, 3, 1);
  }
  // lines
  for (let i = 1; i < n; i++) {
    const a = s.cols[i - 1];
    const b = s.cols[i];
    if (!a || !b) continue;
    pline(c, cx(i - 1), pY(a.pulse), cx(i), pY(b.pulse), "#e05a6a");
    pline(c, cx(i - 1), tY(a.temp), cx(i), tY(b.temp), "#3b6fd1");
  }
  for (let i = 0; i < n; i++) {
    const col = s.cols[i];
    if (!col) continue;
    obox(c, "#ffffff", cx(i) - 1, Math.round(pY(col.pulse)) - 1, 3, 3, "#c0394a");
    R(c, "#3b6fd1", cx(i) - 1, Math.round(tY(col.temp)) - 1, 3, 3);
  }
  if (s.mark != null && s.cols[s.mark]) {
    const col = s.cols[s.mark];
    if (col && Math.floor(now / 400) % 2) {
      c.strokeStyle = "#c0394a";
      c.lineWidth = 1;
      c.beginPath();
      c.ellipse(cx(s.mark) + 0.5, pY(col.pulse) + 0.5, 6, 6, 0, 0, Math.PI * 2);
      c.stroke();
    }
  }
  // RR row
  R(c, "#efe8d2", 0, 74, 192, 9);
  ptext(c, "R", 4, 76, "#3a3f52");
  for (let i = 0; i < n; i++) {
    const col = s.cols[i];
    if (col) ptext(c, String(col.rr), cx(i) - 3, 76, col.rr >= 22 ? "#c0394a" : "#3a3f52");
  }
  // shifts: I/O and drain
  const m = s.shifts.length;
  const sw = (TX1 - TX0) / Math.max(1, m);
  ptext(c, "I", 4, 86, "#3a3f52");
  ptext(c, "O", 4, 93, "#3a3f52");
  for (let k = 0; k < m; k++) {
    const sh = s.shifts[k];
    if (!sh) continue;
    const x = Math.round(TX0 + sw * k);
    R(c, "#e4dcc4", x, 84, 1, 24);
    ptext(c, String(sh.input), x + 2, 86, "#3b5fa8");
    ptext(c, String(sh.urine), x + 2, 93, sh.urine < 400 ? "#c0394a" : "#7a6a1a");
    if (sh.drain !== undefined) {
      obox(c, sh.drainColor ?? FLUID.serous, x + 2, 100, 5, 5, "#5a6070");
      ptext(c, String(sh.drain), x + 9, 100, "#3a3f52");
    }
  }
  // drain legend icon
  obox(c, "#eef3f6", 4, 99, 6, 7, "#5a6070");
  R(c, FLUID.serous, 5, 103, 4, 2);
}

export interface TprHotsCfg {
  onTemp: () => void;
  onPulse: () => void;
  onBp: () => void;
  onRr: () => void;
  onIo: () => void;
  onDrain?: () => void;
}

export function tprHots(o: TprHotsCfg): Hotspot[] {
  const hs: Hotspot[] = [
    { x: 0, y: 10, w: 20, h: 58, label: tr("體溫刻度（藍）", "Temp scale (blue)"), fn: o.onTemp },
    { x: TX1 - 14, y: 10, w: 20, h: 58, label: tr("脈搏刻度（紅）", "Pulse scale (red)"), fn: o.onPulse },
    { x: TX0, y: 10, w: TX1 - 14 - TX0, h: 30, label: tr("脈搏曲線（紅）", "Pulse line (red)"), fn: o.onPulse },
    {
      x: TX0,
      y: 40,
      w: TX1 - 14 - TX0,
      h: 28,
      label: tr("體溫曲線（藍）與血壓（灰）", "Temp line (blue) & BP (gray)"),
      fn: o.onTemp,
    },
    { x: 0, y: 72, w: 192, h: 10, label: tr("呼吸次數", "Resp. rate"), fn: o.onRr },
    { x: 0, y: 83, w: 192, h: 15, label: tr("輸入／尿量（每班）", "Intake / urine (per shift)"), fn: o.onIo },
  ];
  hs.splice(4, 0, { x: 60, y: 0, w: 30, h: 10, label: tr("血壓", "BP"), fn: o.onBp });
  if (o.onDrain)
    hs.push({
      x: 0,
      y: 98,
      w: 192,
      h: 10,
      label: tr("引流量與顏色（每班）", "Drain output & color (per shift)"),
      fn: o.onDrain,
    });
  return hs;
}

/* ---------- furniture ---------- */

/** Ward furniture painters for `Level.furniture`. */
export const WARD_FURNITURE: Record<string, DrawFn> = {
  /** Empty ward bed frame with rails; the level draws the patient on top. */
  wbed(c, px, py) {
    R(c, "#6d7590", px + 1, py, 14, 3);
    R(c, "#8790a8", px + 1, py + 2, 14, 30);
    R(c, "#f4f4f6", px + 2, py + 3, 12, 28);
    R(c, "#ffffff", px + 3, py + 4, 10, 5);
    R(c, "#9aa0b2", px, py + 10, 1, 14);
    R(c, "#9aa0b2", px + 15, py + 10, 1, 14);
  },
  ivpole(c, px, py) {
    R(c, "#9aa0b2", px + 7, py - 6, 2, 20);
    R(c, "#7a8092", px + 4, py + 13, 8, 2);
    R(c, "#e6f2f8", px + 4, py - 6, 4, 6);
    R(c, "#c9dce8", px + 4, py - 2, 4, 2);
  },
  cabinet(c, px, py) {
    obox(c, "#c8b08a", px + 2, py + 3, 12, 11, "#7a6040");
    R(c, "#b0966e", px + 2, py + 8, 12, 1);
    R(c, "#e8f2f8", px + 9, py + 1, 3, 3);
  },
  curtain(c, px, py, o) {
    const h = (o.h ?? 1) * 16;
    R(c, "#9aa0b2", px + 7, py, 2, 1);
    for (let y = 0; y < h; y += 2) R(c, y % 4 ? "#b8d0c8" : "#a0bcb2", px + 6, py + y, 4, 2);
  },
  conftable(c, px, py, o) {
    const w = (o.w ?? 1) * 16;
    obox(c, "#8e6238", px + 1, py + 2, w - 2, 12, "#5a3a26");
    R(c, "#b98552", px + 1, py + 2, w - 2, 4);
    for (let k = 0; k < (o.w ?? 1); k++) {
      R(c, "#f4f4f6", px + 4 + k * 16, py + 7, 6, 4);
      R(c, "#3b5fa8", px + 5 + k * 16, py + 8, 4, 1);
    }
  },
  screen(c, px, py, o) {
    const w = (o.w ?? 1) * 16;
    R(c, "#3a3f52", px, py, w, 2);
    obox(c, "#f6f7fb", px + 2, py + 2, w - 4, 11, "#7a8092");
    R(c, "#3b5fa8", px + 4, py + 4, w - 8, 2);
    for (let k = 0; k < 3; k++) R(c, "#9aa6c8", px + 5, py + 7 + k * 2, w - 14 - k * 6, 1);
  },
  chairrow(c, px, py, o) {
    for (let k = 0; k < (o.w ?? 1); k++) {
      const qx = px + k * 16;
      R(c, "#3a3f52", qx + 4, py + 4, 8, 7);
      R(c, "#4a5068", qx + 4, py + 4, 8, 2);
      R(c, "#2a2e3e", qx + 5, py + 11, 1, 3);
      R(c, "#2a2e3e", qx + 10, py + 11, 1, 3);
    }
  },
  pathway(c, px, py) {
    obox(c, "#fbf8ee", px + 2, py + 1, 12, 14, "#7a8092");
    R(c, "#3b7a5a", px + 3, py + 2, 10, 2);
    for (let k = 0; k < 4; k++) {
      R(c, "#c8bc98", px + 4, py + 6 + k * 2, 8, 1);
      R(c, cyc(["#5cb06c", "#5cb06c", "#ffd447", "#c8c8d0"], k), px + 3, py + 6 + k * 2, 1, 1);
    }
  },
  census(c, px, py, o) {
    const w = (o.w ?? 1) * 16;
    obox(c, "#ffffff", px + 1, py + 2, w - 2, 12, "#7a8092");
    for (let k = 0; k < 4; k++) {
      R(c, "#3b5fa8", px + 3, py + 4 + k * 2, 3, 1);
      R(c, "#9aa6c8", px + 8, py + 4 + k * 2, w - 12 - ((k * 5) % 9), 1);
    }
    R(c, "#c0394a", px + 8, py + 6, 6, 1);
  },
  dresscart(c, px, py) {
    obox(c, "#c8ccd6", px + 2, py + 3, 12, 10, "#5a6070");
    R(c, "#e8ecf2", px + 3, py + 4, 10, 2);
    R(c, "#f4f4f6", px + 4, py + 7, 3, 3);
    R(c, "#5f86bf", px + 8, py + 7, 4, 2);
    R(c, "#e8d8a8", px + 9, py + 10, 3, 2);
    R(c, INK, px + 3, py + 14, 2, 2);
    R(c, INK, px + 11, py + 14, 2, 2);
  },
  medcart(c, px, py) {
    obox(c, "#5f86bf", px + 2, py + 2, 12, 12, "#2f4f7e");
    for (let k = 0; k < 3; k++) R(c, "#3f6aa0", px + 3, py + 5 + k * 3, 10, 1);
    R(c, "#20263a", px + 3, py + 3, 6, 2);
    R(c, INK, px + 3, py + 14, 2, 2);
    R(c, INK, px + 11, py + 14, 2, 2);
  },
  sanitizer(c, px, py) {
    R(c, "#e8ecf2", px + 6, py + 4, 5, 8);
    R(c, "#5fb0c8", px + 6, py + 7, 5, 4);
    R(c, "#3a3f52", px + 7, py + 2, 3, 2);
  },
  elevator(c, px, py) {
    R(c, "#9aa0b2", px + 1, py, 14, 16);
    R(c, "#c8ccd6", px + 2, py + 1, 5, 15);
    R(c, "#c8ccd6", px + 9, py + 1, 5, 15);
    R(c, "#ffd447", px + 7, py + 3, 2, 2);
  },
};

/** A patient lying in a ward bed on the map (drawn every frame by the level). */
export interface MapPatient {
  hair: string;
  skin: string;
  sheet: string;
  shake?: boolean;
  /** Sitting up: head drawn higher. */
  sitting?: boolean;
}

export function drawMapPatient(c: Ctx, px: number, py: number, p: MapPatient, now: number): void {
  const ox = p.shake ? (Math.floor(now / 70) % 2 ? 1 : -1) : 0;
  const up = p.sitting ? -2 : 0;
  DRAW.bed?.(c, px + ox, py + up, { pt: true, hair: p.hair, skin: p.skin, sheet: p.sheet });
}
